import { api } from "@/api";
import type {
  Campaign,
  CampaignAvailability,
  CampaignListParams,
  CampaignPagedResponse,
  CampaignRecipientPagedResponse,
  CampaignRecipientStatus,
  CampaignResult,
  CampaignResultRecipientFilters,
  CampaignResultRecipient,
  CampaignResultRecipientPagedResponse,
  CampaignResultSummary,
  CampaignResume,
  CampaignRetryFailures,
  CampaignRetryFailuresRequest,
  CreateCampaignRequest,
  CreateCampaignResponse,
  SheetTemplate,
  SheetTemplatePayload,
  SheetTemplatePreview,
  SheetTemplatePreviewRequest,
} from "./campaignTypes";

const base = "v1/campaigns";

type AvailabilityResponse = {
  enabled: boolean;
  company_ids: string[] | null;
};

const getAvailability = async (): Promise<CampaignAvailability> => {
  const { data } = await api.get<AvailabilityResponse>(`${base}/availability`);
  return {
    enabled: Boolean(data?.enabled),
    companyIds: data?.company_ids ?? [],
  };
};

const previewSheetTemplate = async ({
  file,
  companyId,
  csvDelimiter,
  encoding,
  headerRow,
}: SheetTemplatePreviewRequest): Promise<SheetTemplatePreview> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", companyId);
  if (csvDelimiter) formData.append("csv_delimiter", csvDelimiter);
  if (encoding) formData.append("encoding", encoding);
  formData.append("header_row", String(headerRow ?? 1));

  const { data } = await api.post<SheetTemplatePreview>(
    `${base}/sheet-templates/preview`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
};

const getSheetTemplates = async (companyId: string): Promise<SheetTemplate[]> => {
  const { data } = await api.get<SheetTemplate[]>(`${base}/sheet-templates`, {
    params: { company_id: companyId },
  });
  return data;
};

const createSheetTemplate = async (
  payload: SheetTemplatePayload
): Promise<SheetTemplate> => {
  const { data } = await api.post<SheetTemplate>(`${base}/sheet-templates`, payload);
  return data;
};

const updateSheetTemplate = async ({
  id,
  payload,
}: {
  id: string;
  payload: SheetTemplatePayload;
}): Promise<SheetTemplate> => {
  const { data } = await api.put<SheetTemplate>(`${base}/sheet-templates/${id}`, payload);
  return data;
};

const deleteSheetTemplate = async ({
  id,
  companyId,
}: {
  id: string;
  companyId: string;
}): Promise<void> => {
  await api.delete(`${base}/sheet-templates/${id}`, {
    params: { company_id: companyId },
  });
};

const createCampaign = async ({
  file,
  companyId,
  templateName,
  phoneNumberId,
  sheetTemplateId,
  name,
  scheduledAt,
  headerImage,
}: CreateCampaignRequest): Promise<CreateCampaignResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", companyId);
  formData.append("template_name", templateName);
  if (phoneNumberId) formData.append("phone_number_id", phoneNumberId);
  if (sheetTemplateId) formData.append("sheet_template_id", sheetTemplateId);
  if (name) formData.append("name", name);
  if (scheduledAt) formData.append("scheduled_at", scheduledAt);
  if (headerImage) formData.append("header_image", headerImage);

  const { data } = await api.post<CreateCampaignResponse>(base, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

const getCampaigns = async ({
  companyId,
  status,
  startAt,
  endAt,
  page = 1,
  pageSize = 20,
}: CampaignListParams): Promise<CampaignPagedResponse> => {
  const { data } = await api.get<CampaignPagedResponse>(base, {
    params: {
      company_id: companyId,
      status,
      start_at: startAt,
      end_at: endAt,
      page,
      page_size: pageSize,
    },
  });
  return data;
};

const getCampaign = async (campaignId: string): Promise<Campaign> => {
  const { data } = await api.get<Campaign>(`${base}/${campaignId}`);
  return data;
};

const rescheduleCampaign = async ({
  campaignId,
  scheduledAt,
}: {
  campaignId: string;
  scheduledAt: string;
}): Promise<void> => {
  await api.patch(`${base}/${campaignId}/schedule`, { scheduled_at: scheduledAt });
};

const cancelCampaign = async (campaignId: string): Promise<void> => {
  await api.post(`${base}/${campaignId}/cancel`);
};

/**
 * Retoma uma campanha parada em "Enviando"/"Falha". Só os destinatários
 * ainda pendentes recebem; quem já foi enviado ou falhou não entra de novo.
 * 409 quando não há pendente ou quando o disparo ainda está avançando.
 */
const resumeCampaign = async (campaignId: string): Promise<CampaignResume> => {
  const { data } = await api.post<CampaignResume>(`${base}/${campaignId}/resume`);
  return data;
};

/**
 * Reprocessa um grupo de "Falhas por motivo": os destinatários daquele motivo
 * voltam para a fila e a campanha dispara de novo só para eles. 409 quando a
 * campanha ainda está enviando, quando o motivo não se resolve reenviando ou
 * quando o grupo já não existe.
 */
const retryCampaignFailures = async ({
  campaignId,
  failure,
}: {
  campaignId: string;
  failure: CampaignRetryFailuresRequest;
}): Promise<CampaignRetryFailures> => {
  const { data } = await api.post<CampaignRetryFailures>(
    `${base}/${campaignId}/failures/retry`,
    failure
  );
  return data;
};

const getResultSummary = async ({
  companyId,
  startAt,
  endAt,
}: {
  companyId: string;
  startAt?: string;
  endAt?: string;
}): Promise<CampaignResultSummary> => {
  const { data } = await api.get<CampaignResultSummary>(`${base}/results/summary`, {
    params: { company_id: companyId, start_at: startAt, end_at: endAt },
  });
  return data;
};

/** Soft delete: some da listagem; o histórico de envio continua no banco. */
const deleteCampaign = async (campaignId: string): Promise<void> => {
  await api.delete(`${base}/${campaignId}`);
};

const getCampaignRecipients = async ({
  campaignId,
  status,
  page = 1,
  pageSize = 50,
}: {
  campaignId: string;
  status?: CampaignRecipientStatus;
  page?: number;
  pageSize?: number;
}): Promise<CampaignRecipientPagedResponse> => {
  const { data } = await api.get<CampaignRecipientPagedResponse>(
    `${base}/${campaignId}/recipients`,
    { params: { status, page, page_size: pageSize } }
  );
  return data;
};

/** Planilha original da campanha, como Blob (download binário). */
const downloadCampaignFile = async (campaignId: string): Promise<Blob> => {
  const { data } = await api.get(`${base}/${campaignId}/file`, {
    responseType: "blob",
  });
  return data as Blob;
};

/** Detalhe analítico de uma campanha: funil, falhas, cliques, resposta e curva. */
const getCampaignResult = async (campaignId: string): Promise<CampaignResult> => {
  const { data } = await api.get<CampaignResult>(`${base}/${campaignId}/results`);
  return data;
};

const getCampaignResultRecipients = async ({
  campaignId,
  filters,
  page = 1,
  pageSize = 50,
}: {
  campaignId: string;
  filters?: CampaignResultRecipientFilters;
  page?: number;
  pageSize?: number;
}): Promise<CampaignResultRecipientPagedResponse> => {
  const { data } = await api.get<CampaignResultRecipientPagedResponse>(
    `${base}/${campaignId}/results/recipients`,
    {
      params: {
        status: filters?.status,
        responded: filters?.responded,
        clicked: filters?.clicked,
        page,
        page_size: pageSize,
      },
    }
  );
  return data;
};

/** Teto que a API aplica em `page_size` — pedir mais que isso volta para 100. */
const RESULT_RECIPIENTS_MAX_PAGE_SIZE = 500;

/**
 * Junta todas as páginas de destinatários para o CSV.
 *
 * O export é montado no cliente porque a API não tem rota de exportação; com
 * o teto de 5000 linhas por campanha e páginas de 500, são no máximo 10
 * requisições. `totalPages` vem da primeira resposta, então o laço tem fim
 * mesmo se a lista mudar no meio (uma campanha ainda disparando).
 */
const getAllCampaignResultRecipients = async ({
  campaignId,
  filters,
}: {
  campaignId: string;
  filters?: CampaignResultRecipientFilters;
}): Promise<CampaignResultRecipient[]> => {
  const first = await getCampaignResultRecipients({
    campaignId,
    filters,
    page: 1,
    pageSize: RESULT_RECIPIENTS_MAX_PAGE_SIZE,
  });

  const items = [...first.items];

  for (let page = 2; page <= first.total_pages; page += 1) {
    const next = await getCampaignResultRecipients({
      campaignId,
      filters,
      page,
      pageSize: RESULT_RECIPIENTS_MAX_PAGE_SIZE,
    });
    items.push(...next.items);
  }

  return items;
};

export const campaignService = {
  getAvailability,
  previewSheetTemplate,
  getSheetTemplates,
  createSheetTemplate,
  updateSheetTemplate,
  deleteSheetTemplate,
  createCampaign,
  getCampaigns,
  getCampaign,
  rescheduleCampaign,
  cancelCampaign,
  resumeCampaign,
  retryCampaignFailures,
  deleteCampaign,
  getResultSummary,
  getCampaignResult,
  getCampaignResultRecipients,
  getAllCampaignResultRecipients,
  getCampaignRecipients,
  downloadCampaignFile,
};
